import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AxiosResponse } from "axios";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { DateInput } from "../../components/DateInput/DateInput";
import { ProgressSpinner } from "primereact/progressspinner";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { Warehouse, WarehouseOverview } from "../../models/Warehouse";
import { RoleType } from "../../models/Role";
import {
  Product,
  ProductType,
  ProductCategory,
  ProductReservation,
} from "../../models/Product";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { useApplicationContext } from "../../application/ApplicationContext";
import {
  getAllDocuments,
  getProductReservations,
} from "../../services/NewVersionService";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllProducts } from "../../services/ProductService";
import { getAllProductCategories } from "../../services/ProductCategoryService";
import { cloneDeep } from "lodash";
import { Utility } from "../../core/Utility";
import { activeUser } from "../../services/AuthService";
import "./WarehousesOverviewPage.scss";

export const WarehousesOverviewPage = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const { state } = useApplicationContext();
  const businessYear = state.businessYear ?? new Date().getFullYear();

  // dateFrom je 1.1. poslovne godine (nije u UI, samo za upit ka API-ju)
  const dateFrom = new Date(businessYear, 0, 1);
  const yearStart = new Date(businessYear, 0, 1, 0, 0, 1);
  const yearEnd = new Date(businessYear, 11, 31);
  const today = new Date();
  const [dateTo, setDateTo] = useState<Date>(
    businessYear === today.getFullYear() ? today : yearEnd
  );

  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse>(
    activeUser?.role?.name === RoleType.Warehouseman
      ? (activeUser?.warehouse as Warehouse)
      : ({} as Warehouse)
  );
  const [warehousesApi, setWarehousesApi] = useState<Warehouse[]>([]);
  const [productsApi, setProductsApi] = useState<Product[]>([]);
  const [productCategoriesApi, setProductCategoriesApi] = useState<
    ProductCategory[]
  >([]);
  const [productType, setProductType] = useState<string>("Svu robu");
  const [productCategory, setProductCategory] = useState<ProductCategory>();
  const [selectedProduct, setSelectedProduct] = useState<Product>();

  const [mercantileOverview, setMercantileOverview] = useState<
    WarehouseOverview[]
  >([]);
  const [repromaterialOverview, setRepromaterialOverview] = useState<
    WarehouseOverview[]
  >([]);
  const [reservations, setReservations] = useState<ProductReservation[]>([]);
  const [processing, setProcessing] = useState(false);

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  const refreshLookups = async () => {
    const [wResp, pResp, pcResp] = await Promise.all([
      getAllWarehouses(),
      getAllProducts(),
      getAllProductCategories(),
    ]);

    [wResp, pResp, pcResp].forEach(ensureSuccess);

    const wh = wResp?.data?.result ?? [];
    const pr = pResp?.data?.result ?? [];
    const pc = pcResp?.data?.result ?? [];

    setWarehousesApi(wh);
    setProductsApi(pr);
    setProductCategoriesApi(pc);

    return { wh, pr, pc };
  };

  useEffect(() => {
    // get static data from server
    const run = async () => {
      setProcessing(true);
      const { wh, pr, pc } = await refreshLookups();
      // ako dolazimo sa dashboarda – setuj magacin
      const stateWarehouse = location.state as {
        warehouseFromDashboard?: string;
      } | null;
      if (stateWarehouse?.warehouseFromDashboard) {
        const clickedWarehouse = wh.find(
          (item: any) => item?.name === stateWarehouse.warehouseFromDashboard
        );
        if (clickedWarehouse) {
          setSelectedWarehouse(clickedWarehouse as Warehouse);
        }
      }
      // inicijalni proračun
      await recalculateOverviews(wh, pr);
      setProcessing(false);
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const today = new Date();
    const yearEnd = new Date(businessYear, 11, 31);
    setDateTo(businessYear === today.getFullYear() ? today : yearEnd);

    const loadReservations = async () => {
      try {
        const from = new Date(businessYear, 0, 1);
        const to = new Date(businessYear, 11, 31);
        const resp: AxiosResponse = await getProductReservations(
          businessYear,
          from,
          to
        );
        setReservations(resp.data.result ?? []);
      } catch (error) {
        console.log(error);
        setReservations([]);
      }
    };
    loadReservations();
  }, [businessYear]);

  useEffect(() => {
    const run = async () => {
      setProcessing(true);
      await recalculateOverviews(
        warehousesApi,
        productsApi,
      );
      setProcessing(false);
    };
    run();
  }, [
    selectedWarehouse,
    dateTo,
    productCategory,
    selectedProduct,
    businessYear,
    reservations,
  ]);

  useEffect(() => {
    setProductCategory({} as ProductCategory);
    setSelectedProduct({} as Product);
  }, [productType]);

  useEffect(() => {
    setSelectedProduct({} as Product);
  }, [productCategory]);

  const recalculateOverviews = async (
    wh: Warehouse[],
    pr: Product[],
  ) => {
    await Promise.all([
      formatMercantileOverviewData(dateFrom, dateTo, wh, pr),
      formatRepromaterialOverviewData(dateFrom, dateTo, wh, pr),
    ]);
  };

  const formatMercantileOverviewData = async (
    dateFromValue: Date,
    dateToValue: Date,
    wh: Warehouse[],
    pr: Product[],
  ) => {
    const warehouse = wh.find((item) => item.id == selectedWarehouse.id);
    const warehouseName = warehouse?.name || undefined;

    const dateFromMs = new Date(dateFromValue).getTime();
    const dateToMs = new Date(dateToValue).getTime() + 1000 * 60 * 60 * 24; // kraj dana

    let copyOfMercantileOverview: WarehouseOverview[] = [];

    // 1) Povuci ulaze/izlaze merkantile
    const [inputsResp, outputsResp]: [AxiosResponse, AxiosResponse] =
      await Promise.all([
        getAllDocuments(true, true, dateFromValue, dateToValue), // prijemnice merkantile
        getAllDocuments(true, false, dateFromValue, dateToValue), // otpremnice merkantile
      ]);

    const mercantileInputs = inputsResp.data.result;
    const mercantileOutputs = outputsResp.data.result;

    const matchesFilters = (doc: any) => {
      const ts = new Date(doc.date).getTime();
      if (ts < dateFromMs || ts > dateToMs) return false;

      if (warehouseName && doc.warehouseName !== warehouseName) return false;

      const productFromList = pr.find((p) => p.name === doc.productName);

      if (
        productCategory?.id &&
        productFromList?.category?.id !== productCategory.id
      ) {
        return false;
      }

      if (selectedProduct?.id && productFromList?.id !== selectedProduct.id) {
        return false;
      }

      return true;
    };

    const upsert = (doc: any, sign: 1 | -1) => {
      if (!matchesFilters(doc)) return;

      const productFromList = pr.find((p) => p.name === doc.productName);
      const productId = productFromList?.id ?? doc.productId ?? doc.productName;
      const measureUnit =
        productFromList?.measuringUnit ?? doc.measuringUnit ?? "";

      const netto: number = doc.netto ?? 0;
      const srps: number = doc.srps ?? 0;

      const index = copyOfMercantileOverview.findIndex(
        (m) => m.product.id?.toString() === productId.toString()
      );

      if (index === -1) {
        const productObj: Product =
          productFromList ??
          ({
            id: productId,
            name: doc.productName ?? String(productId),
            code: "",
            type: ProductType.MercantileGoods,
          } as Product);

        copyOfMercantileOverview.push({
          id: productId,
          warehouse: warehouse ?? ({} as Warehouse),
          product: productObj,
          input: sign === 1 ? netto : 0,
          inputSrps: sign === 1 ? srps : 0,
          output: sign === -1 ? netto : 0,
          outputSrps: sign === -1 ? srps : 0,
          state: sign === 1 ? netto : -netto,
          stateSrps: sign === 1 ? srps : -srps,
          reservation: 0,
          reservationSrps: 0,
          stateWithReservation: sign === 1 ? netto : -netto,
          stateWithReservationSrps: sign === 1 ? srps : -srps,
          measureUnit,
        });
      } else {
        const item = copyOfMercantileOverview[index];

        if (sign === 1) {
          item.input = (item.input ?? 0) + netto;
          item.inputSrps = (item.inputSrps ?? 0) + srps;
        } else {
          item.output = (item.output ?? 0) + netto;
          item.outputSrps = (item.outputSrps ?? 0) + srps;
        }

        item.state = (item.state ?? 0) + sign * netto;
        item.stateSrps = (item.stateSrps ?? 0) + sign * srps;
        item.stateWithReservation =
          (item.stateWithReservation ?? 0) + sign * netto;
        item.stateWithReservationSrps =
          (item.stateWithReservationSrps ?? 0) + sign * srps;
      }
    };

    // 2) Primeni ulaze
    for (const mercantileInput of mercantileInputs) {
      if (mercantileInput?.isDocumentFinished !== true) continue;
      upsert(mercantileInput, 1);
    }

    // 3) Primeni izlaze
    for (const mercantileOutput of mercantileOutputs) {
      if (mercantileOutput?.isDocumentFinished !== true) continue;
      upsert(mercantileOutput, -1);
    }

    // 4) Rezervacije
    for (const productReservation of reservations) {
      if (
        productReservation.type === ProductType.MercantileGoods &&
        (warehouseName
          ? productReservation.warehouse.name === warehouseName
          : true) &&
        (productCategory?.id
          ? productCategory.id === productReservation?.product?.category?.id
          : true) &&
        (selectedProduct?.id
          ? selectedProduct.id === productReservation?.product?.id
          : true) &&
        new Date(productReservation?.expiryDate as Date).getTime() > dateToMs
      ) {
        const productId = productReservation.product?.id;
        if (!productId) continue;

        const amount = Number(productReservation.amount ?? 0);

        const index = copyOfMercantileOverview.findIndex(
          (m) => m.product?.id === productId
        );

        if (index === -1) {
          copyOfMercantileOverview.push({
            id: productReservation.id,
            warehouse: productReservation.warehouse,
            product: productReservation.product as Product,
            input: 0,
            inputSrps: 0,
            output: 0,
            outputSrps: 0,
            state: 0,
            stateSrps: 0,
            reservation: amount,
            reservationSrps: amount,
            stateWithReservation: -amount,
            stateWithReservationSrps: -amount,
            measureUnit: productReservation.product?.measuringUnit ?? "",
          });
        } else {
          const item = copyOfMercantileOverview[index];
          item.reservation = (item.reservation ?? 0) + amount;
          item.reservationSrps = (item.reservationSrps ?? 0) + amount;
          item.stateWithReservation = (item.stateWithReservation ?? 0) - amount;
          item.stateWithReservationSrps =
            (item.stateWithReservationSrps ?? 0) - amount;
        }
      }
    }
    copyOfMercantileOverview.sort((a, b) =>
      a.product.name.localeCompare(b.product.name, "sr")
    );

    setMercantileOverview(copyOfMercantileOverview);
  };

  const formatRepromaterialOverviewData = async (
    dateFromValue: Date,
    dateToValue: Date,
    wh: Warehouse[],
    pr: Product[],
  ) => {
    const warehouse = wh.find((item) => item.id === selectedWarehouse.id);
    const warehouseName = warehouse?.name || undefined;

    const dateFromMs = new Date(dateFromValue).getTime();
    const dateToMs = new Date(dateToValue).getTime() + 1000 * 60 * 60 * 24; // kraj dana

    let copyOfRepromaterialOverview: WarehouseOverview[] = [];

    // 1) Povuci ulaze/izlaze repromaterijala
    const [reproInputsResp, reproOutputsResp]: [AxiosResponse, AxiosResponse] =
      await Promise.all([
        getAllDocuments(false, true, dateFromValue, dateToValue), // reprom. - prijemnice
        getAllDocuments(false, false, dateFromValue, dateToValue), // reprom. - otpremnice
      ]);

    const repromaterialInputs = reproInputsResp.data.result;
    const repromaterialOutputs = reproOutputsResp.data.result;

    const matchesDoc = (doc: any) => {
      const ts = new Date(doc.date).getTime();
      if (ts < dateFromMs || ts > dateToMs) return false;
      if (warehouseName && doc.warehouseName !== warehouseName) return false;
      return true;
    };

    const accumulateRepro = (doc: any, sign: 1 | -1) => {
      if (doc?.isDocumentFinished !== true) return;
      if (!matchesDoc(doc)) return;

      const productIds = (doc.reproProducts ?? "")
        .split(";")
        .map((x: string) => x.trim())
        .filter((x: string) => x !== "");

      const amounts = (doc.reproAmounts ?? "")
        .split(";")
        .map((x: string) => x.trim())
        .filter((x: string) => x !== "");

      productIds.forEach((prodId: string, idx: number) => {
        const rawAmount = amounts[idx] ?? "0";
        const amount = parseFloat(rawAmount) || 0;

        const productFromList = pr.find(
          (p) => p.id?.toString() === prodId.toString()
        );

        if (
          productCategory?.id &&
          productFromList?.category?.id !== productCategory.id
        ) {
          return;
        }

        if (
          selectedProduct?.id &&
          selectedProduct.id.toString() !== prodId.toString()
        ) {
          return;
        }

        const index = copyOfRepromaterialOverview.findIndex(
          (r) => r.product.id?.toString() === prodId.toString()
        );

        const measureUnit = productFromList?.measuringUnit ?? "Kom";

        if (index === -1) {
          const productObj: Product =
            productFromList ??
            ({
              id: prodId,
              name: String(prodId),
              type: ProductType.RawMaterial,
            } as Product);

          copyOfRepromaterialOverview.push({
            id: prodId,
            warehouse: warehouse ?? ({} as Warehouse),
            product: productObj,
            input: sign === 1 ? amount : 0,
            output: sign === -1 ? amount : 0,
            state: sign === 1 ? amount : -amount,
            reservation: 0,
            stateWithReservation: sign === 1 ? amount : -amount,
            measureUnit,
          });
        } else {
          const item = copyOfRepromaterialOverview[index];

          if (sign === 1) {
            item.input = (item.input ?? 0) + amount;
          } else {
            item.output = (item.output ?? 0) + amount;
          }

          item.state = (item.state ?? 0) + sign * amount;
          item.stateWithReservation =
            (item.stateWithReservation ?? 0) + sign * amount;
        }
      });
    };

    // 2) Primeni ulaze
    for (const repromaterialInput of repromaterialInputs) {
      accumulateRepro(repromaterialInput, 1);
    }

    // 3) Primeni izlaze
    for (const repromaterialOutput of repromaterialOutputs) {
      accumulateRepro(repromaterialOutput, -1);
    }

    // 4) Rezervacije repromaterijala
    for (const productReservation of reservations) {
      if (
        productReservation.type === ProductType.RawMaterial &&
        (warehouseName
          ? productReservation.warehouse?.name === warehouseName
          : true) &&
        new Date(productReservation?.expiryDate as Date).getTime() > dateToMs
      ) {
        const remainingProducts = JSON.parse(
          productReservation.reproProductsRemainingReservationJSON as string
        ) as Product[];

        for (const repromaterialProduct of remainingProducts) {
          const prodId = repromaterialProduct.id;
          const productFromList = pr.find(
            (p) => p.id?.toString() === prodId?.toString()
          );

          if (
            productCategory?.id &&
            productFromList?.category?.id !== productCategory.id
          ) {
            continue;
          }

          if (
            selectedProduct?.id &&
            selectedProduct.id.toString() !== prodId?.toString()
          ) {
            continue;
          }

          if (!repromaterialProduct.amount) continue;
          const amount = parseFloat(repromaterialProduct.amount as string) || 0;

          const index = copyOfRepromaterialOverview.findIndex(
            (r) => r.product.id?.toString() === prodId?.toString()
          );

          if (index === -1) {
            const productObj: Product =
              productFromList ??
              ({
                id: prodId,
                name: String(prodId),
                type: ProductType.RawMaterial,
              } as Product);

            copyOfRepromaterialOverview.push({
              id: productReservation.id,
              warehouse: productReservation.warehouse,
              product: productObj,
              input: 0,
              output: 0,
              state: 0,
              reservation: amount,
              stateWithReservation: -amount,
              measureUnit: productObj.measuringUnit ?? "Kom",
            });
          } else {
            const item = copyOfRepromaterialOverview[index];
            item.reservation = (item.reservation ?? 0) + amount;
            item.stateWithReservation =
              (item.stateWithReservation ?? 0) - amount;
          }
        }
      }
    }

    // 5) Filtriraj samo proizvode koji realno postoje u products
    const filteredCopyOfRepromaterialOverview: WarehouseOverview[] = [];
    copyOfRepromaterialOverview.forEach((oneReprom) => {
      const productIndex = pr.findIndex((p) => p.id === oneReprom.product.id);
      if (productIndex > -1)
        filteredCopyOfRepromaterialOverview.push(oneReprom);
    });
    filteredCopyOfRepromaterialOverview.sort((a, b) =>
      a.product.name.localeCompare(b.product.name, "sr")
    );

    setRepromaterialOverview(filteredCopyOfRepromaterialOverview);
  };

  const onSelectedWarehouseChange = (property: string, value: string) => {
    let copyOfNewWarehouse = cloneDeep(selectedWarehouse);
    copyOfNewWarehouse = warehousesApi.find(
      (item) => item.id == value
    ) as Warehouse;
    setSelectedWarehouse(copyOfNewWarehouse);
  };

  const onDateToChange = (property: string, value: Date | null) => {
    setDateTo(value as Date);
  };

  const onProductTypeChange = (property: string, value: string) => {
    setProductType(value);
  };

  const handleProductCategoryChange = (prop: string, value: any) => {
    const copyOfNewProductCategory = productCategoriesApi.find(
      (item) => item.id == value
    ) as ProductCategory;
    setProductCategory(copyOfNewProductCategory);
  };

  const onSelectedProductChange = (property: string, value: string) => {
    const copyOfNewSelectedProduct = productsApi.find(
      (item) => item.id == value
    ) as Product;
    setSelectedProduct(copyOfNewSelectedProduct);
  };

  const mercantileInput = (rowData: WarehouseOverview) => {
    return Utility.addThousandSeparator(rowData?.input as number);
  };

  const mercantileInputSrps = (rowData: WarehouseOverview) => {
    return Utility.addThousandSeparator(rowData?.inputSrps as number);
  };

  const mercantileOutput = (rowData: WarehouseOverview) => {
    return Utility.addThousandSeparator(rowData?.output as number);
  };

  const mercantileOutputSrps = (rowData: WarehouseOverview) => {
    return Utility.addThousandSeparator(rowData?.outputSrps as number);
  };

  const mercantileState = (rowData: WarehouseOverview) => {
    return Utility.addThousandSeparator(rowData?.state as number);
  };

  const mercantileStateSrps = (rowData: WarehouseOverview) => {
    return Utility.addThousandSeparator(rowData?.stateSrps as number);
  };

  const mercantileReservation = (rowData: WarehouseOverview) => {
    return Utility.addThousandSeparator(rowData?.reservation as number);
  };

  const mercantileReservationSrps = (rowData: WarehouseOverview) => {
    return Utility.addThousandSeparator(rowData?.reservationSrps as number);
  };

  const mercantileStateWithReservation = (rowData: WarehouseOverview) => {
    return Utility.addThousandSeparator(
      rowData?.stateWithReservation as number
    );
  };

  const mercantileStateWithReservationSrps = (rowData: WarehouseOverview) => {
    return Utility.addThousandSeparator(
      rowData?.stateWithReservationSrps as number
    );
  };

  const renderMercantileHeader = () => {
    return (
      <div className="mercantile-header">
        <span>{t("MERKANTILNA ROBA")}</span>
      </div>
    );
  };

  const renderRepromaterialHeader = () => {
    return (
      <div className="repromaterial-header">
        <span>{t("REPROMATERIJAL")}</span>
      </div>
    );
  };

  const sapCode = (rowData: any) => {
    return rowData.product?.code;
  };

  const sapRepromaterialCode = (rowData: any) => {
    return productsApi.find((item) => item.id == rowData.product.id)?.code;
  };

  const productName = (rowData: any) => {
    return rowData.product?.name;
  };

  const repromaterialProductName = (rowData: any) => {
    const productIndex = productsApi.findIndex((p) => p.id === rowData.product.id);
    const productName = productIndex > -1 ? productsApi[productIndex].name : "";
    return productName;
  };

  const repromaterialInput = (rowData: any) => {
    return !Number.isInteger(rowData.input)
      ? rowData.input.toFixed(3)
      : rowData.input.toFixed(0);
  };

  const repromaterialOutput = (rowData: any) => {
    return !Number.isInteger(rowData.output)
      ? rowData.output.toFixed(3)
      : rowData.output.toFixed(0);
  };

  const repromaterialState = (rowData: any) => {
    return !Number.isInteger(rowData.state)
      ? rowData.state.toFixed(3) == 0 ||
        rowData.state.toFixed(3) == 0.001 ||
        rowData.state.toFixed(3) == -0.001
        ? "0"
        : rowData.state.toFixed(3)
      : rowData.state.toFixed(0);
  };

  const repromaterialReservation = (rowData: any) => {
    return !Number.isInteger(rowData.reservation)
      ? rowData.reservation.toFixed(3)
      : rowData.reservation.toFixed(0);
  };

  const repromaterialStateWithReservation = (rowData: any) => {
    return !Number.isInteger(rowData.stateWithReservation)
      ? rowData.stateWithReservation.toFixed(3) == 0 ||
        rowData.stateWithReservation.toFixed(3) == 0.001 ||
        rowData.stateWithReservation.toFixed(3) == -0.001
        ? "0"
        : rowData.stateWithReservation.toFixed(3)
      : rowData.stateWithReservation.toFixed(0);
  };

  const {
    control,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    mode: "all",
  });

  return (
    <PageLayout>
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-shop-window header-title-icon"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
              }}
            ></i>
            <span className="header-title">{t("Stanje magacina")}</span>
            <DropdownInput
              id="selectedWarehouse"
              value={selectedWarehouse?.id}
              onValueChange={onSelectedWarehouseChange}
              options={warehousesApi.filter((item) => item.virtual !== true)}
              optionValue="id"
              optionLabel="name"
              labelText=""
              placeholderText={t("Magacin")}
              control={control}
              errors={errors}
              disabled={
                activeUser?.role?.name === RoleType.Warehouseman || processing
              }
            />
            <span className="header-elements">{t("na dan")}</span>
            <DateInput
              id="dateTo"
              value={dateTo}
              onValueChange={onDateToChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
            <span className="header-elements">{t("za")}</span>
            <DropdownInput
              id="productType"
              value={productType}
              onValueChange={onProductTypeChange}
              options={[
                { label: t("Svu robu"), value: "Svu robu" },
                { label: t("Merkantila"), value: ProductType.MercantileGoods },
                { label: t("Repromaterijal"), value: ProductType.RawMaterial },
              ]}
              labelText=""
              placeholderText=""
              control={control}
              errors={errors}
              disabled={processing}
            />
            {productType !== "Svu robu" && (
              <span className="header-elements">{t("za kategoriju")}</span>
            )}
            {productType !== "Svu robu" && (
              <DropdownInput
                id="selectedProductGroup"
                value={productCategory?.id}
                onValueChange={handleProductCategoryChange}
                optionValue="id"
                optionLabel="name"
                options={productCategoriesApi.filter(
                  (item) => item.type === productType
                )}
                labelText=""
                placeholderText={t("Kategorija robe")}
                control={control}
                errors={errors}
                disabled={processing}
              />
            )}
            {productType !== "Svu robu" && productCategory && (
              <span className="header-elements">{t("za robu")}</span>
            )}
            {productType !== "Svu robu" && productCategory && (
              <DropdownInput
                id="selectedProduct"
                value={selectedProduct?.id}
                onValueChange={onSelectedProductChange}
                optionValue="id"
                optionLabel="name"
                filter={true}
                options={productsApi.filter(
                  (item) => item.category?.id === productCategory?.id
                )}
                labelText=""
                placeholderText={t("Roba")}
                control={control}
                errors={errors}
                disabled={!(productCategory && productCategory?.id)}
              />
            )}
          </div>
        </div>
        {processing ? (
          <ProgressSpinner
            style={{ width: "100px", height: "100px", color: "green" }}
            strokeWidth="4"
            animationDuration=".8s"
          />
        ) : (
          (productType === "Svu robu" ||
            productType === ProductType.MercantileGoods) && (
            <div className="table-body">
              <DataTable
                header={renderMercantileHeader}
                value={mercantileOverview}
                showGridlines
                stripedRows
              >
                <Column body={sapCode} header={t("SAP šifra")} />
                <Column body={productName} header={t("Naziv proizvoda")} />
                <Column field="measureUnit" header={t("Merna jed.")} />
                <Column body={mercantileInput} header={t("Ulaz robe")} />
                <Column body={mercantileInputSrps} header={t("SRPS")} />
                <Column body={mercantileOutput} header={t("Izlaz robe")} />
                <Column body={mercantileOutputSrps} header={t("SRPS")} />
                <Column body={mercantileState} header={t("Stanje robe")} />
                <Column body={mercantileStateSrps} header={t("SRPS")} />
                <Column
                  body={mercantileReservation}
                  header={t("Rezervisana roba")}
                />
                <Column body={mercantileReservationSrps} header={t("SRPS")} />
                <Column
                  body={mercantileStateWithReservation}
                  header={t("Stanje sa rezervacijom")}
                />
                <Column
                  body={mercantileStateWithReservationSrps}
                  header={t("SRPS")}
                />
              </DataTable>
            </div>
          )
        )}
        {!processing &&
          (productType === "Svu robu" ||
            productType === ProductType.RawMaterial) && (
            <div className="table-body-repromaterial">
              <DataTable
                header={renderRepromaterialHeader}
                value={repromaterialOverview}
                showGridlines
                stripedRows
              >
                <Column body={sapRepromaterialCode} header={t("SAP šifra")} />
                <Column
                  body={repromaterialProductName}
                  header={t("Naziv proizvoda")}
                />
                <Column field="measureUnit" header={t("Merna jed.")} />
                <Column body={repromaterialInput} header={t("Ulaz robe")} />
                <Column body={repromaterialOutput} header={t("Izlaz robe")} />
                <Column body={repromaterialState} header={t("Stanje robe")} />
                {/* <Column
                  body={repromaterialReservation}
                  header={t("Rezervisana roba")}
                />
                <Column
                  body={repromaterialStateWithReservation}
                  header={t("Stanje sa rezervacijom")}
                /> */}
              </DataTable>
            </div>
          )}
      </div>
    </PageLayout>
  );
};
